import { useState } from 'react';
import { Bell, Moon, Sun } from 'lucide-react';
import Avatar from '../ui/Avatar';
import IconButton from '../ui/IconButton';
import CountBadge from '../ui/CountBadge';
import ProfileMenu from './ProfileMenu';
import { SOCKET_STATUS_LABELS } from '../../lib/chatLabels';
import { absoluteAssetUrl } from '../../lib/chatMappers';

const MAIN_SITE = 'https://z2market.com';

/**
 * Sidebar top bar: brand mark, then theme → unread → profile.
 *
 * The brand mark is the site's own wordmark. Two cuts of that one mark are
 * served, and .z2-brand-logo-light / .z2-brand-logo-dark pick between them
 * (index.css):
 *
 *   /logo.svg       the badge as the main site draws it: a blue-to-violet pill
 *                   with the wordmark punched out of it. Reads correctly on the
 *                   light sidebar.
 *   /logo-mono.png  the same wordmark keyed out of the same artwork in white on
 *                   transparency, derived by scripts/local/qa/make-chat-logo-mono.mjs.
 *                   Used in dark mode: the pill's bright plate is what clashed
 *                   with the dark sidebar, and the cut-out letters carry the
 *                   brand on their own.
 *
 * Both files share the same 4:1 frame, so the two never change size as the theme
 * flips. Clicking returns to the marketplace - plain anchor, same tab.
 *
 * All external links here and in ProfileMenu are absolute on purpose: this app
 * runs on chat.z2market.com, so "/login" or "/buyer-dashboard/orders" would 404.
 */
export default function SidebarHeader({
  user,
  unreadCount = 0,
  onOpenNotifications,
  notificationsOpen = false,
  socketStatus = 'idle',
  isDark,
  onToggleTheme,
}) {
  const [menuOpen, setMenuOpen] = useState(false);

  const displayName = user?.displayName || user?.username || 'Your account';
  // storeAvatarUrl first, matching navbar.js - otherwise a user who uploaded a
  // store logo sees only initials.
  const avatarSrc = absoluteAssetUrl(user?.storeAvatarUrl || user?.avatar);
  const isLive = socketStatus === 'open';

  const statusLabel = SOCKET_STATUS_LABELS[socketStatus] ?? 'Chat';

  return (
    /*
     * Layout contract for this row - one line at every width, never a stack:
     *   px-5 / sm:px-4    the logo always has a real gutter, never flush with
     *                     the viewport edge
     *   two flex groups   brand (left) | controls (right), separated by
     *                     `justify-between` with `ml-auto` on the controls, so
     *                     the empty middle collapses first and the gaps inside
     *                     each group stay even
     *   shrink-0 pill     the status keeps its own width instead of being
     *                     squeezed under the theme button (that overlap is what
     *                     the owner reported - see screenshots/chat-header/
     *                     README.md); the label is switched off by breakpoint in
     *                     .z2-header-status-text (index.css), because the
     *                     desktop column is narrower than a phone
     */
    <header className="relative flex items-center justify-between gap-2 px-5 pt-4 pb-3 sm:px-4">
      <div className="flex min-w-0 items-center gap-2.5">
        <a
          href={MAIN_SITE}
          title="Back to Z2Market"
          aria-label="Z2Market - back to the marketplace"
          className="z2-glow-hover flex shrink-0 items-center rounded-xl"
        >
          {/* Height drives the size on purpose: both cuts are 4:1, so `w-auto`
              keeps the mark in proportion at every breakpoint. A fixed
              max-width used to squash it to 3.5:1 on phones.
              28px (112px wide) is also the size the 320px column can afford:
              the mark + status pill + three 40px controls have to fit inside
              287px of content box, which the 32px version (128px wide) misses.
              The two cuts are swapped with display, not opacity, so only the
              visible one is ever painted or announced. */}
          <img
            src="/logo.svg"
            alt="Z2Market"
            width="200"
            height="50"
            className="z2-brand-logo-light h-7 w-auto"
          />
          <img
            src="/logo-mono.png"
            alt="Z2Market"
            width="1200"
            height="300"
            className="z2-brand-logo-dark h-7 w-auto"
          />
        </a>

        <span
          className="flex shrink-0 items-center gap-1.5 text-[10.5px] leading-tight text-ink-3"
          title={statusLabel}
          aria-label={`Connection status: ${statusLabel}`}
        >
          <span
            aria-hidden="true"
            className={`h-1.5 w-1.5 shrink-0 rounded-full ${isLive ? 'bg-online' : 'bg-away'}`}
          />
          {/* Below 384px there is no room for a legible label, so the pill keeps
              its dot plus the `title`/`aria-label` and drops the text. See
              .z2-header-status-text in index.css for the full breakpoint map.
              No `truncate` here on purpose: the label is shown only at widths
              that fit it whole, and a truncating flex item would collapse to
              its min-content width instead. */}
          <span className="z2-header-status-text whitespace-nowrap">{statusLabel}</span>
        </span>
      </div>

      {/* `ml-auto` keeps the controls pinned right even when the brand group
          collapses or the status pill is empty. */}
      <div className="ml-auto flex shrink-0 items-center gap-1.5">
        <IconButton
          size="brand"
          label={isDark ? 'Switch to light mode' : 'Switch to soft dark mode'}
          onClick={onToggleTheme}
        >
          {isDark ? <Sun size={17} /> : <Moon size={17} />}
        </IconButton>

        <IconButton
          size="brand"
          label={
            unreadCount > 0
              ? `Show ${unreadCount} unread message${unreadCount === 1 ? '' : 's'}`
              : 'No unread messages'
          }
          active={notificationsOpen}
          onClick={onOpenNotifications}
        >
          <Bell size={17} />
          {unreadCount > 0 && (
            <CountBadge
              value={unreadCount}
              tone="brand"
              className="absolute -top-0.5 -right-0.5 ring-2 ring-surface"
            />
          )}
        </IconButton>

        <button
          type="button"
          title={displayName}
          aria-label={`Profile menu for ${displayName}`}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
          className="z2-glow-hover ml-0.5 grid h-11 w-11 place-items-center rounded-full sm:h-10 sm:w-10"
        >
          <Avatar name={displayName} src={avatarSrc} size="headerSm" />
        </button>
      </div>

      <ProfileMenu open={menuOpen} onClose={() => setMenuOpen(false)} user={user} />
    </header>
  );
}
